import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { MfaSignInForm } from '@/features/auth/mfa-sign-in-form';
import { type SigninErrorReason, useMfaSignin, useSignin } from '@/hooks/auth';
import { sanitizeReturnTo } from '@/utils/return-to';
import { emailSchema, passwordSchema } from '@/utils/schemas';

const signInSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

type SignInForm = z.infer<typeof signInSchema>;

const RATE_LIMITED_MESSAGE =
  '短時間にログインが繰り返されました。しばらく時間をおいてから再度お試しください。';

const SIGNIN_ERROR_MESSAGES: Record<SigninErrorReason, string> = {
  invalid_credentials: 'メールアドレスまたはパスワードが誤っています',
  profile_failed: 'ログインに失敗しました',
  rate_limited: RATE_LIMITED_MESSAGE,
};

export const SignInPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const passwordChanged = location.state?.passwordChanged === true;
  // 未ログインで踏んだリンク等の戻り先。外部URLを渡されても遷移しないようここで検証する
  const redirectTo = sanitizeReturnTo(searchParams.get('next')) ?? '/';

  // MFA が有効なユーザーはログインの API が短命トークンを返すので、コード入力に切り替える
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaErrorMessage, setMfaErrorMessage] = useState<string | undefined>(undefined);

  const { mutate: signinRequest, isPending } = useSignin({
    redirectTo,
    onSuccess: () => toast.success('ログインしました'),
    onMfaRequired: (token) => {
      setMfaErrorMessage(undefined);
      setMfaToken(token);
    },
    onError: (reason) => toast.error(SIGNIN_ERROR_MESSAGES[reason]),
  });

  const { mutate: mfaSigninRequest, isPending: isMfaPending } = useMfaSignin({
    redirectTo,
    onSuccess: () => toast.success('ログインしました'),
    onError: (detail) => {
      switch (detail.reason) {
        case 'invalid_code':
          setMfaErrorMessage(detail.message);
          break;
        case 'expired':
          // 短命トークンの期限切れ。最初からやり直してもらう
          setMfaToken(null);
          toast.error('時間切れのため、もう一度ログインしてください');
          break;
        case 'rate_limited':
          toast.error(RATE_LIMITED_MESSAGE);
          break;
        default:
          toast.error('ログインに失敗しました');
      }
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit = (data: SignInForm) => {
    signinRequest({ username: data.email, password: data.password });
  };

  return (
    <Fragment>
      <title>ログイン - アニマルオフィス</title>

      <div className="flex-1 flex items-center justify-center py-10">
        <div className="w-[400px] px-5 sm:px-0 mx-auto">
          <div className="mb-5">
            <h2 className="h2">ログイン</h2>
            {passwordChanged && !mfaToken && (
              <p role="status" className="mt-3 flex gap-2 text-sm">
                <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-green-700" />
                パスワードを変更しました。新しいパスワードでログインしてください。
              </p>
            )}
          </div>

          {mfaToken ? (
            <MfaSignInForm
              email={getValues('email')}
              onSubmit={(mfaCode) => {
                setMfaErrorMessage(undefined);
                mfaSigninRequest({ mfaToken, mfaCode });
              }}
              onBack={() => setMfaToken(null)}
              isPending={isMfaPending}
              errorMessage={mfaErrorMessage}
              onCodeTypeChange={() => setMfaErrorMessage(undefined)}
            />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2 mb-5">
                <div>
                  <Label required className="mb-1">
                    メールアドレス
                  </Label>
                  <input {...register('email')} type="email" />
                  <FormError message={errors.email?.message} />
                </div>

                <div>
                  <Label required className="mb-1">
                    パスワード
                  </Label>
                  <PasswordInput {...register('password')} />
                  <FormError message={errors.password?.message} />
                  <Link
                    to="/password/reset"
                    state={{ email: watch('email') }}
                    className="text-sm text-neutral-600 mt-2 text-right underline block hover:text-neutral-900"
                  >
                    パスワードを忘れた場合
                  </Link>
                </div>
              </div>
              <div className="flex flex-col items-center gap-4">
                <Button type="submit" fullWidth disabled={isPending || !isValid} loading={isPending}>
                  ログインする
                </Button>
                <p className="text-sm text-neutral-600">
                  アカウントをお持ちでない方は
                  <Link to="/register" className="text-sky-800 hover:text-sky-900 underline">
                    新規登録
                  </Link>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </Fragment>
  );
};
